import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { t } from "./i18n/translations";

// Pages
import Home from "./pages/Home";
import Solution from "./pages/Solution";
import Sports from "./pages/Sports";
import Tarifs from "./pages/Tarifs";
import About from "./pages/About";
import Contact from "./pages/Contact";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Cookie Banner Component
const CookieBanner = () => {
  const [show, setShow] = useState(false);
  const { language } = useLanguage();
  
  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      setTimeout(() => setShow(true), 2000);
    }
  }, []);
  
  const handleAccept = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    setShow(false);
  };
  
  const handleDecline = () => {
    localStorage.setItem('cookie_consent', 'declined');
    setShow(false);
  };
  
  if (!show) return null;
  
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t shadow-lg" data-testid="cookie-banner">
      <div className="container-asp flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground text-center sm:text-left">
          {t('cookies.message', language)}{' '}
          <a href="/confidentialite" className="text-primary underline">{t('cookies.privacyPolicy', language)}</a>.
        </p>
        <div className="flex gap-3 flex-shrink-0">
          <button
            onClick={handleDecline}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="cookie-decline-btn"
          >
            {t('cookies.decline', language)}
          </button>
          <button
            onClick={handleAccept}
            className="px-6 py-2 text-sm bg-primary text-white rounded-md hover:bg-primary/90 transition-colors font-medium"
            data-testid="cookie-accept-btn"
          >
            {t('cookies.accept', language)}
          </button>
        </div>
      </div>
    </div>
  );
};

// Legal Pages (simplified)
const LegalPage = ({ title, content }) => (
  <Layout>
    <div className="pt-32 pb-20 bg-slate-50">
      <div className="container-asp">
        <h1 className="font-heading font-bold text-4xl mb-8">{title}</h1>
        <div className="bg-white rounded-xl p-8 shadow-sm prose max-w-none">
          <p className="text-muted-foreground">{content}</p>
        </div>
      </div>
    </div>
  </Layout>
);

const MentionsLegales = () => (
  <LegalPage 
    title="Mentions Légales" 
    content="Audio Sports Performance - Startup deeptech spécialisée dans l'entraînement audio-cognitif pour sports collectifs. Siège social : Bordeaux, France. Contact : contact@audiosportsperformance.com. Hébergeur : Vercel Inc. Ces mentions légales sont conformes aux exigences de la loi pour la confiance dans l'économie numérique (LCEN)."
  />
);

const Confidentialite = () => (
  <LegalPage 
    title="Politique de Confidentialité" 
    content="Audio Sports Performance s'engage à protéger vos données personnelles. Les informations collectées via nos formulaires sont utilisées uniquement pour répondre à vos demandes et améliorer nos services. Conformément au RGPD, vous disposez d'un droit d'accès, de rectification et de suppression de vos données. Contact : contact@audiosportsperformance.com."
  />
);

const Cookies = () => (
  <LegalPage 
    title="Politique des Cookies" 
    content="Notre site utilise des cookies techniques nécessaires au bon fonctionnement du site. Nous n'utilisons pas de cookies publicitaires ou de tracking tiers. Vous pouvez gérer vos préférences de cookies à tout moment via les paramètres de votre navigateur."
  />
);

function App() {
  useEffect(() => {
    // Ensure any previous Coming Soon gate is cleared
    localStorage.setItem('asp_access_granted', 'true');
  }, []);

  return (
    <LanguageProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/solution" element={<Layout><Solution /></Layout>} />
            <Route path="/sports" element={<Layout><Sports /></Layout>} />
            <Route path="/tarifs" element={<Layout><Tarifs /></Layout>} />
            <Route path="/a-propos" element={<Layout><About /></Layout>} />
            <Route path="/contact" element={<Layout><Contact /></Layout>} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/confidentialite" element={<Confidentialite />} />
            <Route path="/cookies" element={<Cookies />} />
          </Routes>
          <CookieBanner />
          <Toaster position="top-right" />
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
